/**
 * Runtime registry: built-in jewelry profiles + admin-defined profiles from site settings.
 */

import { BUILTIN_JEWELRY_PROFILES } from './jewelryBuiltinProfiles';

let mergedProfiles = { ...BUILTIN_JEWELRY_PROFILES };

/** @param {Record<string, object>} profiles */
export function setJewelryProfiles(profiles) {
  if (!profiles || typeof profiles !== 'object' || Array.isArray(profiles)) {
    mergedProfiles = { ...BUILTIN_JEWELRY_PROFILES };
    return;
  }
  mergedProfiles = { ...profiles };
}

/** @param {object} settings site settings from /api/settings.php */
export function applyJewelryProfilesFromSettings(settings) {
  const fromApi = settings?.jewelry_profiles;
  if (fromApi && typeof fromApi === 'object' && !Array.isArray(fromApi)) {
    setJewelryProfiles(fromApi);
    return;
  }
  const hidden = new Set(
    Array.isArray(settings?.jewelry_profiles_config?.hiddenBuiltinIds)
      ? settings.jewelry_profiles_config.hiddenBuiltinIds
      : []
  );
  const custom = Array.isArray(settings?.jewelry_profiles_config?.custom)
    ? settings.jewelry_profiles_config.custom
    : [];
  const overrides =
    settings?.jewelry_profiles_config?.builtinOverrides &&
    typeof settings.jewelry_profiles_config.builtinOverrides === 'object'
      ? settings.jewelry_profiles_config.builtinOverrides
      : {};
  const next = {};
  for (const [id, profile] of Object.entries(BUILTIN_JEWELRY_PROFILES)) {
    if (hidden.has(id)) continue;
    if (overrides[id]) {
      next[id] = { ...profile, ...overrides[id], builtin: true };
    } else {
      next[id] = profile;
    }
  }
  for (const profile of custom) {
    if (profile?.id) next[profile.id] = profile;
  }
  setJewelryProfiles(next);
}

export function getJewelryProfiles() {
  return mergedProfiles;
}

export function getJewelryProfileIds() {
  return Object.keys(mergedProfiles);
}

/** @param {string} id */
export function getJewelryProfile(id) {
  const key = String(id || '').trim().toLowerCase();
  return key ? mergedProfiles[key] || null : null;
}

export const JEWELRY_FIELD_TEMPLATES = [
  {
    key: 'photo',
    label: 'Photo upload',
    defaults: { id: 'photo', type: 'photo', label: 'Photo upload', required: true, hint: 'Clear front-facing photo works best.' },
  },
  {
    key: 'chain_length',
    label: 'Chain length',
    defaults: {
      id: 'chain_length',
      type: 'select',
      label: 'Chain length',
      optionsKey: 'chain',
      optional: true,
      adminLabel: 'Offer chain length',
    },
  },
  {
    key: 'ring_size',
    label: 'Ring size',
    defaults: { id: 'ring_size', type: 'select', label: 'Ring size', optionsKey: 'ring', required: true },
  },
  {
    key: 'bracelet_size',
    label: 'Bracelet size',
    defaults: { id: 'bracelet_size', type: 'select', label: 'Bracelet size', optionsKey: 'bracelet', required: true },
  },
  {
    key: 'engraving',
    label: 'Engraving text',
    defaults: { id: 'engraving', type: 'text', label: 'Engraving text', required: true, maxLength: 40 },
  },
  {
    key: 'note',
    label: 'Customer notes',
    defaults: {
      id: 'note',
      type: 'text',
      label: 'Special instructions',
      optional: true,
      adminLabel: 'Special instructions field',
      maxLength: 200,
    },
  },
  {
    key: 'custom_select',
    label: 'Custom dropdown',
    defaults: {
      id: 'option',
      type: 'select',
      label: 'Choose option',
      required: true,
      options: [{ value: 'a', label: 'Option A', priceDelta: 0 }],
    },
  },
];

/** @param {object} field */
export function resolveFieldTemplateKey(field) {
  if (!field?.type) return 'photo';
  if (field.type === 'photo') return 'photo';
  if (field.type === 'select') {
    if (field.optionsKey) {
      const tpl = JEWELRY_FIELD_TEMPLATES.find((t) => t.defaults?.optionsKey === field.optionsKey);
      if (tpl) return tpl.key;
    }
    return 'custom_select';
  }
  if (field.type === 'text') {
    const byId = JEWELRY_FIELD_TEMPLATES.find(
      (t) => t.defaults?.type === 'text' && t.defaults?.id === field.id
    );
    if (byId) return byId.key;
    return field.optional ? 'note' : 'engraving';
  }
  return 'photo';
}

/** @param {object} templateEntry @param {object} [overrides] */
export function fieldFromTemplate(templateEntry, overrides = {}) {
  const base = templateEntry?.defaults ? { ...templateEntry.defaults } : {};
  return { ...base, ...overrides };
}

export function slugifyProfileId(label) {
  return String(label || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 48);
}
